// doctor-frontend/src/pages/intern/ActivityStartWizard.jsx
// Activity Launcher for guided activities.
// No-regression launcher reframing:
// - Preserves tenant-safe guided activity start, lazy run creation, citizen context, roster centre prefill, session-expiry handling, and OPD/Visit separation.
// - Reframes the launcher as two switchable views: Quick Launch — Activities by Training Area and Full Guided Activity Catalogue.
// - Uses fourteen professional training-area tiles without duplicate quick-launch buttons; one template has one primary quick-launch home.
// - Promotes the existing VHND/VHSND, NTEP/TB, and IPC templates into their appropriate quick-launch training areas.
// - Keeps cross-cutting concepts (for example counselling and National Health Programmes) available without duplicating the same template in multiple quick-launch groups.
// - Keeps proposed-but-not-yet-seeded activities out of launch actions until a real backend ActivityTemplate exists.
// - Preserves user-facing Core Practice Area terminology and keeps OPD / Primary Care inside Comprehensive Services.
// - Keeps Quick Launch tiles compact: two activities are visible by default, with More / Show less for larger groups.
// - Uses accurate active-template counters instead of implying that planned catalogue expansion is already complete.
// - Uses three Quick Launch columns on standard desktop widths and removes redundant per-tile catalogue buttons.

import React, { useEffect, useMemo, useState } from 'react';
import {
  Box, Paper, Typography, Button, Alert, Divider, Card, CardContent,
  Chip, Tooltip
} from '@mui/material';
// removed LocalHospitalIcon (no longer used)
// import LocalHospitalIcon from '@mui/icons-material/LocalHospital';
import SearchIcon from '@mui/icons-material/Search';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import { useLocation, useNavigate } from 'react-router-dom';
import { activityApi } from '../../services/activityApi';
import ActivityCatalogGrid from './components/ActivityCatalogGrid';

// Core Practice Areas, Competencies & Skills groups (formerly "Quick Start by Category")
// User-facing protected/established terminology used by larger platforms is intentionally avoided.
// Safer product terminology: Core Practice Area → Competency → Skill.
const CORE_PRACTICE_AREA_LABEL = 'Core Practice Area';
const PROD_API_BASE_FALLBACK = 'https://aj-portal-backend-demo.onrender.com';

function sanitizeDisplayText(value) {
  const input = value == null ? '' : String(value).trim();
  if (!input) return '';

  // Keep legacy/internal database keys intact; sanitize only text shown or sent as display metadata.
  // Search terms are split so this file does not reintroduce retired terminology in visible grep checks.
  const short = ['E', 'P', 'A'].join('\\.?');
  const full = [
    ['Ent', 'rustable'].join(''),
    ['Pro', 'fessional'].join(''),
    ['Act', 'ivities'].join(''),
  ].join('\\s+');

  return input
    .replace(new RegExp(`${full}\\s*\\(\\s*${short}s\\s*\\)`, 'gi'), `${CORE_PRACTICE_AREA_LABEL}s`)
    .replace(new RegExp(full, 'gi'), `${CORE_PRACTICE_AREA_LABEL}s`)
    .replace(new RegExp(`\\b${short}s\\b`, 'gi'), `${CORE_PRACTICE_AREA_LABEL}s`)
    .replace(new RegExp(`\\b${short}\\b`, 'gi'), CORE_PRACTICE_AREA_LABEL);
}

function sanitizeDisplayArray(values) {
  if (Array.isArray(values)) return values.map((item) => sanitizeDisplayText(item)).filter(Boolean);
  return String(values || '')
    .split(/[|,]/g)
    .map((item) => sanitizeDisplayText(item))
    .filter(Boolean);
}

function listStorageKeys(store) {
  try {
    const out = [];
    const len = Number(store?.length || 0);
    for (let i = 0; i < len; i += 1) {
      const key = store.key(i);
      if (key) out.push(key);
    }
    return out;
  } catch {
    return [];
  }
}

function getStorage(kind) {
  try {
    if (typeof window === 'undefined') return null;
    return kind === 'session' ? window.sessionStorage : window.localStorage;
  } catch {
    return null;
  }
}

const ACTIVITY_GROUPS = [
  {
    id: 'maternal-family-planning',
    title: 'Maternal, Postnatal & Family Planning',
    description: 'Pregnancy, childbirth, postnatal mother-newborn care and family-planning practice.',
    accent: '#C2185B',
    soft: '#FCE4EC',
    items: [
      { label: 'Antenatal Care & Danger-Sign Counselling', templateLabel: 'Antenatal counselling & danger signs', slugs: ['antenatal-counselling-danger-signs'] },
      { label: 'Conduct Normal Delivery (NVD)', templateLabel: 'Conduct Normal Delivery (NVD)', slugs: ['nvd', 'conduct-nvd'] },
      { label: 'Postnatal Mother & Newborn Care', templateLabel: 'Postnatal mother + newborn check', slugs: ['postnatal-mother-newborn-check'] },
      { label: 'Family Planning', templateLabel: 'Counselling – Family planning', slugs: ['counselling-family-planning'] },
    ],
  },
  {
    id: 'child-adolescent-nutrition',
    title: 'Newborn, Child, Adolescent & Nutrition',
    description: 'Under-five care, growth, feeding, nutrition and child/adolescent counselling.',
    accent: '#2E7D32',
    soft: '#E8F5E9',
    items: [
      { label: 'Under-5 Sick Child / IMNCI', templateLabel: 'Under-5 assessment (IMNCI / malnutrition)', slugs: ['imnci-under5'] },
      { label: 'Growth Monitoring by Anthropometry', templateLabel: 'Growth monitoring by anthropometry', slugs: ['growth-monitoring-anthropometry'] },
      { label: 'Breastfeeding Assessment & Support', templateLabel: 'Counselling – Breastfeeding', slugs: ['counselling-breastfeeding'] },
      { label: 'IYCF Assessment & Counselling', templateLabel: 'Counselling – IYCF', slugs: ['counselling-iycf'] },
      { label: 'Kangaroo Mother Care', templateLabel: 'Counselling – Kangaroo Mother Care', slugs: ['counselling-kangaroo-mother-care'] },
      { label: 'Child Nutrition / Malnutrition Counselling', templateLabel: 'Counselling – Nutritional interventions', slugs: ['counselling-nutritional-interventions'] },
      { label: 'Diarrhoea Counselling', templateLabel: 'Counselling – Diarrhoea', slugs: ['counselling-diarrhoea'] },
      { label: 'ARI Counselling', templateLabel: 'Counselling – ARI', slugs: ['counselling-ari'] },
    ],
  },
  {
    id: 'immunization-vaccine-practice',
    title: 'Immunization & Vaccine Practice',
    description: 'UIP session work, vaccine delivery, cold chain, safety, communication and documentation.',
    accent: '#1565C0',
    soft: '#E3F2FD',
    items: [
      { label: 'Immunization Session (UIP)', templateLabel: 'Immunization', slugs: ['uip-immunization', 'uip-immunization-session'] },
      { label: 'Cold-Chain Monitoring', templateLabel: 'Monitoring of cold chain', slugs: ['cold-chain-monitoring'] },
      { label: 'Vaccine Wastage Factor', templateLabel: 'Calculate wastage factor of vaccines', slugs: ['calculate-vaccine-wastage-factor'] },
      { label: 'AEFI Recording & Reporting', templateLabel: 'Recording and reporting of AEFI', slugs: ['aefi-recording-reporting'] },
      { label: 'Four Key Immunization Messages', templateLabel: 'Give 4 key messages of immunization', slugs: ['four-key-messages-immunization'] },
      { label: 'BCG Vaccination', templateLabel: 'Perform vaccination: BCG', slugs: ['vaccination-bcg'] },
      { label: 'Pentavalent Vaccination', templateLabel: 'Perform vaccination: Pentavalent', slugs: ['vaccination-pentavalent'] },
      { label: 'Measles / MR Vaccination', templateLabel: 'Perform vaccination: Measles', slugs: ['vaccination-measles'] },
      { label: 'Other / Newer Vaccines', templateLabel: 'Perform vaccination: Other newer vaccines', slugs: ['vaccination-other-newer-vaccines'] },
      { label: 'Yellow Fever Vaccination & Documentation', templateLabel: 'Yellow fever vaccination & documentation', slugs: ['yellow-fever-vaccination-documentation'] },
      { label: 'Optional & Travel Vaccine Counselling', templateLabel: 'Counselling – Optional & travel vaccines', slugs: ['counselling-optional-travel-vaccines'] },
    ],
  },
  {
    id: 'ncd-chronic-care',
    title: 'NCD & Chronic Disease Care',
    description: 'Cardiometabolic, respiratory and endocrine assessment plus long-term management skills.',
    accent: '#00796B',
    soft: '#E0F2F1',
    items: [
      { label: 'Assessment of Hypertension', templateLabel: 'Assessment of hypertension', slugs: ['assessment-hypertension'] },
      { label: 'Assessment of Hyperglycaemia / Diabetes', templateLabel: 'Assessment of hyperglycaemia', slugs: ['assessment-hyperglycaemia'] },
      { label: 'Assessment of Dyslipidaemia', templateLabel: 'Assessment of dyslipidemia', slugs: ['assessment-dyslipidemia'] },
      { label: 'Assessment of COPD', templateLabel: 'Assessment of COPD', slugs: ['assessment-copd'] },
      { label: 'Assessment of Hypothyroidism', templateLabel: 'Assessment of hypothyroidism', slugs: ['assessment-hypothyroidism'] },
      { label: 'NCD Lifestyle & Adherence Counselling', templateLabel: 'Counselling for NCD (lifestyle / adherence)', slugs: ['counselling-ncd-lifestyle-adherence'] },
      { label: 'Basic Pharmacological Management of NCD', templateLabel: 'Pharmacological management of NCD (basic)', slugs: ['pharmacological-management-ncd-basic'] },
    ],
  },
  {
    id: 'cancer-prevention-screening',
    title: 'Cancer Prevention & Screening',
    description: 'Risk communication, early diagnosis and common cancer-screening skills.',
    accent: '#7B1FA2',
    soft: '#F3E5F5',
    items: [
      { label: 'Preventable Cancer & Early-Diagnosis Counselling', templateLabel: 'Counselling for preventable cancers / early diagnosis', slugs: ['counselling-preventable-cancers-early-diagnosis'] },
      { label: 'Clinical Breast Examination', templateLabel: 'Screening – Breast examination', slugs: ['screening-breast-examination'] },
      { label: 'Oral Examination', templateLabel: 'Screening – Oral examination', slugs: ['screening-oral-examination'] },
      { label: 'Cervical Screening (VIA)', templateLabel: 'Screening – Cervical (VIA)', slugs: ['screening-cervical-via'] },
    ],
  },
  {
    id: 'mental-health-substance-use',
    title: 'Mental Health & Substance Use',
    description: 'Structured screening, safety assessment, brief counselling and referral.',
    accent: '#3949AB',
    soft: '#E8EAF6',
    items: [
      { label: 'Depression Screening', templateLabel: 'Screening for depression (standard scale)', slugs: ['screening-depression-standard-scale'] },
      { label: 'Dependence / Substance-Use Screening', templateLabel: 'Screening for dependence (standard scale)', slugs: ['screening-dependence-standard-scale'] },
    ],
  },
  {
    id: 'national-health-programmes',
    title: 'National Health Programmes & Programme Practice',
    description: 'Programme-specific guided work, with programme links also remaining cross-cutting in relevant activities.',
    accent: '#00838F',
    soft: '#E0F7FA',
    items: [
      { label: 'TB Screening, Notification & Contact Management (NTEP)', templateLabel: 'TB Screening/Notification & Contact Management (NTEP)', slugs: ['ntep-tb-workflow'] },
    ],
  },
  {
    id: 'emergency-trauma-referral',
    title: 'Emergency, Trauma & Referral',
    description: 'Initial assessment, red flags, first response, escalation and safe referral.',
    accent: '#D84315',
    soft: '#FBE9E7',
    items: [
      { label: 'Emergency Triage, Red Flags & Referral', templateLabel: 'Emergency triage, red flags & referral (BLS/ALS basics)', slugs: ['emergency-triage-red-flags-referral'] },
    ],
  },
  {
    id: 'counselling-communication',
    title: 'Counselling & Communication',
    description: 'Standalone counselling practice; counselling also remains embedded inside relevant clinical and programme activities.',
    accent: '#6D4C41',
    soft: '#EFEBE9',
    items: [
      { label: 'General Counselling', templateLabel: 'Counselling', slugs: ['counselling-general'] },
    ],
  },
  {
    id: 'community-outreach-health-promotion',
    title: 'Community Outreach & Health Promotion',
    description: 'Community sessions, outreach, health education, health-day observance and community engagement.',
    accent: '#558B2F',
    soft: '#F1F8E9',
    items: [
      { label: 'Village Health, Sanitation & Nutrition Day (VHSND/VHND)', templateLabel: 'Village Health, Sanitation & Nutrition Day (VHSND/VHND)', slugs: ['vhnd-session'] },
    ],
  },
  {
    id: 'surveillance-environment-occupational',
    title: 'Surveillance, Environment & Occupational Health',
    description: 'Surveillance, outbreak work, community census/mapping, environmental and occupational assessment.',
    accent: '#EF6C00',
    soft: '#FFF3E0',
    items: [],
  },
  {
    id: 'health-systems-management-quality',
    title: 'Health Systems, Management & Quality',
    description: 'IPC, quality, reporting, supervision, supplies and health-system management.',
    accent: '#455A64',
    soft: '#ECEFF1',
    items: [
      { label: 'Infection Prevention & Control (IPC) — Universal', templateLabel: 'Infection Prevention & Control (IPC) — Universal', slugs: ['ipc-safety'] },
    ],
  },
  {
    id: 'geriatric-palliative-rehabilitation',
    title: 'Geriatric, Palliative & Rehabilitation Care',
    description: 'Older-person assessment, supportive/palliative needs and rehabilitation-oriented care.',
    accent: '#5E35B1',
    soft: '#EDE7F6',
    items: [],
  },
  {
    id: 'inclusive-sexual-gender-lgbtqia',
    title: 'Inclusive Sexual, Gender & LGBTQIA+ Health',
    description: 'Inclusive primary care, sexual-health communication and gender-diverse care navigation.',
    accent: '#AD1457',
    soft: '#FCE4EC',
    items: [],
  },
];


const QUICK_TEMPLATE_MATCHES = {
  'Conduct Normal Delivery': {
    slugs: ['nvd', 'conduct-nvd'],
    titles: ['Conduct Normal Delivery (NVD)', 'Conduct Normal vaginal delivery'],
  },
  'Conduct Normal Delivery (NVD)': {
    slugs: ['nvd', 'conduct-nvd'],
    titles: ['Conduct Normal Delivery (NVD)', 'Conduct Normal vaginal delivery'],
  },
  'Conduct Normal vaginal delivery': {
    slugs: ['nvd', 'conduct-nvd'],
    titles: ['Conduct Normal Delivery (NVD)', 'Conduct Normal vaginal delivery'],
  },
  'Emergency triage, red flags & referral (BLS/ALS basics)': {
    slugs: ['emergency-triage-red-flags-referral'],
    titles: ['Emergency triage, red flags & referral (BLS/ALS basics)'],
  },
  'Under-5 assessment (IMNCI / malnutrition)': {
    slugs: ['imnci-under5'],
    titles: ['Under-5 Sick Child (IMNCI/IMCI)', 'Under-5 assessment (IMNCI / malnutrition)'],
  },
  Immunization: {
    slugs: ['uip-immunization', 'uip-immunization-session'],
    titles: ['Immunization Session (UIP)', 'Immunization'],
  },
  'Monitoring of cold chain': {
    slugs: ['cold-chain-monitoring'],
    titles: ['Monitoring of cold chain'],
  },
  'Growth monitoring by anthropometry': {
    slugs: ['growth-monitoring-anthropometry'],
    titles: ['Growth monitoring by anthropometry'],
  },
};

const OPD_HANDOFF_TEMPLATE_KEYS = new Set(['opd-primary-care']);
const OPD_HANDOFF_TEMPLATE_TITLES = new Set([
  'opd / primary care consult (phc/uphc/chc)',
]);

function getActiveCitizen() {
  try { return JSON.parse(localStorage.getItem('activeCitizen') || 'null') || null; }
  catch { return null; }
}

function normalizeCentreLabel(value) {
  const raw = String(value || '').trim();
  const v = raw.toLowerCase().replace(/[_-]+/g, ' ').replace(/\s+/g, ' ').trim();
  if (!v) return '';
  if (v.includes('keru')) return 'CRHA Keru';
  if (v.includes('dhawa')) return 'CRHA Dhawa';
  if (v.includes('pratap')) return 'UHTC Pratap Nagar';
  if (v.includes('dundhara') || v.includes('dhundhara') || v.includes('dhundara') || v.includes('dundara')) {
    return 'CRHA Dhundhara';
  }
  return raw;
}

function safeJsonParse(v) {
  try { return JSON.parse(v); } catch { return null; }
}

function readTenantSlugFromStorage() {
  const candidates = [];
  try {
    const t1 = localStorage.getItem('tenantSlug');
    const t2 = localStorage.getItem('activeTenantSlug');
    const t3 = sessionStorage.getItem('tenantSlug');
    const t4 = sessionStorage.getItem('activeTenantSlug');
    if (t1) candidates.push(t1);
    if (t2) candidates.push(t2);
    if (t3) candidates.push(t3);
    if (t4) candidates.push(t4);

    const lu = safeJsonParse(localStorage.getItem('loggedInUser') || 'null');
    const su = safeJsonParse(sessionStorage.getItem('loggedInUser') || 'null');
    [lu, su].filter(Boolean).forEach((obj) => {
      const core = obj?.user || obj;
      candidates.push(
        core?.tenantSlug,
        obj?.tenantSlug,
        core?.tenant?.slug,
        obj?.tenant?.slug
      );
    });
  } catch {}

  const slug = candidates.map(s => String(s || '').trim()).find(Boolean);
  return slug || '';
}

function getTenantSlug() {
  try {
    const m = (window.location.pathname || '').match(/^\/t\/([^/]+)/);
    if (m) return decodeURIComponent(m[1]);
  } catch {}

  return readTenantSlugFromStorage();
}

function ensureTenantSlugStored() {
  try {
    const slug = getTenantSlug();
    if (slug) {
      localStorage.setItem('tenantSlug', slug);
      localStorage.setItem('activeTenantSlug', slug);
      sessionStorage.setItem('tenantSlug', slug);
      sessionStorage.setItem('activeTenantSlug', slug);
    }
  } catch {}
}

function isObjectId(v) {
  return typeof v === 'string' && /^[a-f\d]{24}$/i.test(v.trim());
}

function getTenantId() {
  try {
    const candidates = [];

    const t1 = localStorage.getItem('tenantId');
    const t2 = localStorage.getItem('activeTenantId');
    const t3 = sessionStorage.getItem('tenantId');
    const t4 = sessionStorage.getItem('activeTenantId');
    if (t1) candidates.push(t1);
    if (t2) candidates.push(t2);
    if (t3) candidates.push(t3);
    if (t4) candidates.push(t4);

    const lu = safeJsonParse(localStorage.getItem('loggedInUser') || 'null');
    const su = safeJsonParse(sessionStorage.getItem('loggedInUser') || 'null');
    [lu, su].filter(Boolean).forEach((obj) => {
      const core = obj?.user || obj;
      candidates.push(
        core?.tenantId,
        obj?.tenantId,
        core?.tenant?._id,
        core?.tenant?.id,
        obj?.tenant?._id,
        obj?.tenant?.id
      );
    });

    const hit = candidates
      .map((x) => (x == null ? '' : String(x)).trim())
      .find((x) => isObjectId(x));

    return hit || '';
  } catch {
    return '';
  }
}

function getAuthToken() {
  const pick = (store) => {
    try {
      const keys = listStorageKeys(store);
      const preferred = [
        'cmfm_token',
        'cmfmToken',
        'tenant_token',
        'tenantToken',
        'portal_token',
        'portalToken',
        'doctor_token',
        'doctorToken',
        'authToken',
        'jwt',
        'token',
      ];

      for (const name of preferred) {
        const hit = keys.find((key) => key.toLowerCase() === String(name).toLowerCase());
        if (hit) {
          const value = store.getItem(hit);
          if (value) return value;
        }
      }

      const scoped = keys.find(
        (key) => /cmfm|tenant|portal|doctor/i.test(key) && /token/i.test(key) && !/owner|citizen/i.test(key)
      );
      if (scoped) return store.getItem(scoped);

      const nonOwnerCitizen = keys.find((key) => /token/i.test(key) && !/owner|citizen/i.test(key));
      return nonOwnerCitizen ? store.getItem(nonOwnerCitizen) : null;
    } catch {
      return null;
    }
  };
  return pick(getStorage('local')) || pick(getStorage('session'));
}

function tenantUrl(p) {
  const slug = getTenantSlug();
  const path = String(p || '');
  if (!slug) return path;
  if (!path.startsWith('/')) return `/t/${slug}/${path}`.replace(/\/{2,}/g, '/');
  if (path.startsWith(`/t/${slug}`)) return path;
  return `/t/${slug}${path}`.replace(/\/{2,}/g, '/');
}

function getTenantLoginUrl() {
  return tenantUrl('/cmfm-login');
}

function isSessionExpiredError(error) {
  const status = Number(
    error?.status ||
    error?.response?.status ||
    error?.data?.status ||
    error?.response?.data?.status
  );

  const message = [
    error?.message,
    error?.data?.message,
    error?.data?.error,
    error?.response?.data?.message,
    error?.response?.data?.error,
  ]
    .filter(Boolean)
    .join(' ')
    .toLowerCase();

  if (status === 401) return true;

  const hasAuthExpiryMessage = /\b(jwt expired|token expired|session expired|invalid token|no token provided|unauthori[sz]ed)\b/.test(message);
  return status === 403 && hasAuthExpiryMessage;
}

function getRuntimeApiBase() {
  try {
    const cfg = window.__CMFM_CONFIG__ || window.__APP_CONFIG__ || window.__PORTAL_CONFIG__ || window.runtimeConfig || {};
    const hit = cfg.apiBaseUrl || cfg.apiBase || cfg.API_BASE_URL || cfg.API_BASE;
    return String(hit || '').trim().replace(/\/+$/, '');
  } catch {
    return '';
  }
}

function getApiBase() {
  try {
    const runtime = getRuntimeApiBase();
    if (runtime && !runtime.startsWith('/')) return runtime;
  } catch {}

  try {
    const raw = String(
      process.env.REACT_APP_API_BASE_URL ||
      process.env.REACT_APP_API_BASE ||
      ''
    ).trim().replace(/\/+$/, '');
    if (raw && !raw.startsWith('/')) {
      try {
        const { hostname, port, protocol, origin } = window.location || {};
        const isLocal = hostname === 'localhost' || hostname === '127.0.0.1';
        const isCra = String(port) === '3000';
        if (isLocal && isCra && (raw === origin || /:3000$/i.test(raw))) {
          return `${protocol}//${hostname}:5000`;
        }
      } catch {}
      return raw;
    }
  } catch {}

  try {
    const { hostname, port, protocol } = window.location || {};
    if ((hostname === 'localhost' || hostname === '127.0.0.1') && String(port) === '3000') {
      return `${protocol}//${hostname}:5000`;
    }
  } catch {}

  return PROD_API_BASE_FALLBACK;
}

function apiUrl(path) {
  const p = String(path || '');
  if (/^https?:\/\//i.test(p)) return p;
  const base = getApiBase();
  return `${base}${p.startsWith('/') ? p : `/${p}`}`.replace(/([^:]\/)\/+/g, '$1');
}

async function fetchJson(path, { method = 'GET', json, headers } = {}) {
  const baseHeaders = {
    Accept: 'application/json',
    'X-Requested-From': 'portal-tenant',
    'X-Client': 'doctor-frontend',
    ...(headers || {}),
  };
  if (json !== undefined) baseHeaders['Content-Type'] = 'application/json';

  const tok = getAuthToken();
  if (tok) baseHeaders.Authorization = `Bearer ${tok}`;

  const slug = getTenantSlug();
  if (slug) baseHeaders['X-Tenant-Slug'] = slug;

  const tenantId = getTenantId();
  if (tenantId) baseHeaders['X-Tenant-Id'] = tenantId;

  const body = json !== undefined ? JSON.stringify(json) : undefined;
  const plain = String(path || '').startsWith('/') ? String(path || '') : `/${path}`;
  const candidates = [];
  const tenantScoped = tenantUrl(plain);
  candidates.push(tenantScoped);
  if (tenantScoped !== plain) candidates.push(plain);

  let lastResult = null;
  for (const candidate of candidates) {
    const r = await fetch(apiUrl(candidate), {
      method,
      headers: baseHeaders,
      body,
      credentials: 'include',
    });

    const rawText = await r.text().catch(() => '');
    let data = {};
    if (rawText) {
      try { data = JSON.parse(rawText); }
      catch { data = { message: rawText }; }
    }

    if (r.ok) return data;
    lastResult = { status: r.status, data };

    if (![400, 404, 405, 501].includes(Number(r.status))) break;
  }

  const msg = lastResult?.data?.error || lastResult?.data?.message || `Request failed: ${lastResult?.status || 'unknown'}`;
  const e = new Error(msg);
  e.status = lastResult?.status;
  e.data = lastResult?.data;
  throw e;
}

function clearVisitContext(navigate, location) {
  try {
    const KEYS = [
      'activeVisitId',
      'activeVisit',
      'currentVisitId',
      'visitId',
      'VISIT_ID',
      'VIS_ACTIVE',
      'visitContext',
      'visitHeader',
    ];
    [localStorage, sessionStorage].forEach(store => {
      try {
        KEYS.forEach(k => store.removeItem(k));
        for (const k of listStorageKeys(store)) {
          if (/visit/i.test(k) || /^VIS-/.test(k)) store.removeItem(k);
        }
      } catch {}
    });
    try { window.__activeVisit = null; } catch {}
    try {
      const sp = new URLSearchParams(location.search || '');
      let changed = false;
      ['visitId', 'visit', 'v', 'vid'].forEach(p => {
        if (sp.has(p)) {
          sp.delete(p);
          changed = true;
        }
      });
      if (changed) {
        navigate(
          {
            pathname: location.pathname,
            search: sp.toString() ? `?${sp}` : '',
          },
          { replace: true }
        );
      }
    } catch {}
    try { window.dispatchEvent(new CustomEvent('visit:cleared')); } catch {}
  } catch {}
}

function normalizeRole(value) {
  return String(value || '')
    .trim()
    .toLowerCase()
    .replace(/[_-]+/g, ' ')
    .replace(/\s+/g, ' ');
}

function readRoleFromStorage() {
  try {
    const candidates = [];
    const lu = safeJsonParse(localStorage.getItem('loggedInUser') || 'null');
    const su = safeJsonParse(sessionStorage.getItem('loggedInUser') || 'null');

    [lu, su].filter(Boolean).forEach((obj) => {
      const core = obj?.user || obj;
      candidates.push(core?.role, obj?.role, core?.userType, obj?.userType);
    });

    const hit = candidates
      .map((x) => String(x || '').trim())
      .find(Boolean);

    return hit || 'Intern';
  } catch {
    return 'Intern';
  }
}

function toTitleCaseRole(value) {
  const r = normalizeRole(value);
  const map = {
    intern: 'Intern',
    jr: 'Junior Resident',
    'junior resident': 'Junior Resident',
    sr: 'Senior Resident',
    'senior resident': 'Senior Resident',
    faculty: 'Faculty',
    admin: 'Admin',
    administrator: 'Admin',
    hod: 'HOD',
    owner: 'Owner',
    'super admin': 'Super Admin',
    deo: 'DEO',
  };

  return map[r] || value || 'Intern';
}

function slugifyKey(value) {
  return String(value || '')
    .trim()
    .toLowerCase()
    .replace(/[_\s]+/g, '-')
    .replace(/[^a-z0-9-]/g, '')
    .replace(/-+/g, '-')
    .replace(/^-|-$/g, '');
}

function normalizeTemplateRow(template, index = 0) {
  const raw = template && typeof template === 'object' ? template : {};
  const item = raw.item && typeof raw.item === 'object' ? raw.item : raw;
  const id = String(item._id || item.id || item.templateId || item.slug || `tpl-${index}`).trim();
  const title = sanitizeDisplayText(item.title || item.templateTitle || item.templateName || item.name || '');
  const slug = String(item.slug || item.templateSlug || item.activityKey || '').trim();
  const activityKey = slugifyKey(item.activityKey || slug || title);
  const category = sanitizeDisplayText(item.category || item.templateCategory || item.finalGroup || 'General');
  const finalGroup = sanitizeDisplayText(item.finalGroup || item.templateCategory || category || 'General');
  const corePracticeAreaTitle = sanitizeDisplayText(
    item.corePracticeAreaTitle ||
    item.practiceAreaTitle ||
    finalGroup ||
    category ||
    title
  );
  const practiceAreaTitle = sanitizeDisplayText(item.practiceAreaTitle || item.corePracticeAreaTitle || corePracticeAreaTitle);

  return {
    ...item,
    id,
    _id: item._id || id,
    title,
    name: sanitizeDisplayText(item.name || item.templateName || title),
    templateName: sanitizeDisplayText(item.templateName || item.name || title),
    templateTitle: title,
    slug,
    templateSlug: item.templateSlug || slug,
    activityKey,
    category,
    templateCategory: sanitizeDisplayText(item.templateCategory || category),
    finalGroup,
    corePracticeAreaTitle,
    practiceAreaTitle,
    goals: sanitizeDisplayArray(item.goals || []),
    competencies: sanitizeDisplayArray(item.competencies || []),
    skills: sanitizeDisplayArray(item.skills || []),
    rubricHints: sanitizeDisplayArray(item.rubricHints || []),
    scheduleFit: sanitizeDisplayArray(item.scheduleFit || []),
    tags: sanitizeDisplayArray(item.tags || []),
    reportingTarget: item.reportingTarget || {},
    sourceFlow: item.sourceFlow || 'guided',
    active: item.active !== false,
    _raw: item,
  };
}

function templateToCatalogActivity(t, group = null) {
  const template = normalizeTemplateRow(t);
  const id = String(template.id || template._id || template.slug || template.title).trim();
  const sourceCategory = template.category || template.finalGroup || 'Training';
  const category = group?.title || sourceCategory;
  const title = template.title || template.templateName || 'Guided Activity';
  const tags = mergeUnique([
    'guided',
    'workflow',
    category,
    sourceCategory,
    template.finalGroup,
    template.slug,
    template.activityKey,
    template.corePracticeAreaTitle,
    template.practiceAreaTitle,
    ...(template.tags || []),
    ...(template.competencies || []),
    ...(template.skills || []),
  ]);

  return {
    id: `tpl-${id}`,
    code: template.slug || template.activityKey || slugifyKey(title).toUpperCase(),
    pilotKey: template.activityKey || template.slug || slugifyKey(title),
    pilotKeys: mergeUnique([template.slug, template.activityKey, title, template.corePracticeAreaTitle, slugifyKey(title)]),
    templateId: template._id || template.id || id,
    title,
    category,
    sourceCategory,
    groupKey: group?.id || '',
    groupAccent: group?.accent || '#475569',
    groupSoft: group?.soft || '#F1F5F9',
    tags,
    estimatedMinutes: Number(template.estimatedMinutes || template.durationMinutes || 5) || 5,
    difficulty: template.difficulty || 'Medium',
    requiresCitizen: template.requiresCitizen === true,
    requiresVisit: template.requiresVisit === true,
    description: sanitizeDisplayText(template.description || `Guided activity template: ${title}`),
    roles: template.roles || ['Intern', 'Junior Resident', 'Senior Resident', 'Faculty', 'Admin'],
    corePracticeAreaTitle: template.corePracticeAreaTitle,
    practiceAreaTitle: template.practiceAreaTitle,
    finalGroup: template.finalGroup,
    competencies: template.competencies || [],
    skills: template.skills || [],
    learningDomains: sanitizeDisplayArray(
      template.learningDomains ||
      template.educationalAlignment?.domains ||
      []
    ),
    bloomLevel: sanitizeDisplayText(
      template.bloomLevel ||
      template.bloom ||
      template.educationalAlignment?.bloom ||
      ''
    ),
    millerLevel: sanitizeDisplayText(
      template.millerLevel ||
      template.miller ||
      template.educationalAlignment?.miller ||
      ''
    ),
    reportingTarget: template.reportingTarget || {},
    _rawTemplate: template,
  };
}

function mergeUnique(values) {
  return Array.from(
    new Set(
      (values || [])
        .map((v) => String(v || '').trim())
        .filter(Boolean)
    )
  );
}

function pickArrayPayload(data) {
  if (Array.isArray(data?.items)) return data.items;
  if (Array.isArray(data?.rows)) return data.rows;
  if (Array.isArray(data?.data)) return data.data;
  if (Array.isArray(data?.templates)) return data.templates;
  if (Array.isArray(data?.results)) return data.results;
  if (Array.isArray(data)) return data;
  return [];
}

function looksLikeTemplateIdentifier(value) {
  const s = String(value || '').trim();
  return Boolean(s && !/^tpl-fallback-only$/i.test(s));
}

function normalizedTemplateKeys(template) {
  return mergeUnique([
    template?.slug,
    template?.templateSlug,
    template?.activityKey,
  ]).map((value) => slugifyKey(value));
}

function normalizedTemplateTitles(template) {
  return mergeUnique([
    template?.title,
    template?.templateTitle,
    template?.name,
    template?.templateName,
  ]).map((value) => sanitizeDisplayText(value).toLowerCase());
}

function isOpdHandoffTemplate(template) {
  if (!template) return false;
  if (normalizedTemplateKeys(template).some((key) => OPD_HANDOFF_TEMPLATE_KEYS.has(key))) return true;
  return normalizedTemplateTitles(template).some((title) => OPD_HANDOFF_TEMPLATE_TITLES.has(title));
}

function quickItemMatchesTemplate(item, template) {
  if (!item || !template) return false;

  const acceptedSlugs = new Set(
    (Array.isArray(item.slugs) ? item.slugs : [])
      .map((value) => slugifyKey(value))
      .filter(Boolean)
  );
  const templateKeys = normalizedTemplateKeys(template);
  if (templateKeys.some((key) => acceptedSlugs.has(key))) return true;

  const acceptedTitles = new Set(
    [item.templateLabel, item.label, ...(Array.isArray(item.titles) ? item.titles : [])]
      .map((value) => sanitizeDisplayText(value).toLowerCase())
      .filter(Boolean)
  );
  return normalizedTemplateTitles(template).some((title) => acceptedTitles.has(title));
}

function findActivityGroupForTemplate(template) {
  return ACTIVITY_GROUPS.find((group) =>
    group.items.some((item) => quickItemMatchesTemplate(item, template))
  ) || null;
}

function toLocalDateKey(value) {
  if (!value) return '';
  const raw = String(value).trim();
  const direct = raw.match(/^(\d{4}-\d{2}-\d{2})/);
  if (direct) return direct[1];

  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return '';
  const pad = (n) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
}

function todayLocalDateKey() {
  return toLocalDateKey(new Date());
}

function rosterRowCentre(row = {}) {
  return normalizeCentreLabel(row?.centre || row?.center || row?.location || '');
}

function rosterRowDateBounds(row = {}) {
  const singleDate = toLocalDateKey(row?.date || row?.dutyDate || row?.rosterDate);
  const fromDate = toLocalDateKey(
    row?.fromDate || row?.startDate || row?.from || row?.postingFrom || singleDate
  );
  const toDate = toLocalDateKey(
    row?.toDate || row?.endDate || row?.to || row?.postingTo || singleDate
  );
  return { fromDate, toDate };
}

function rosterRowCoversDate(row = {}, dateKey = '') {
  if (!dateKey || row?.isOnDuty === false || row?.isOnLeave === true) return false;
  const { fromDate, toDate } = rosterRowDateBounds(row);
  if (!fromDate && !toDate) return false;
  if (fromDate && dateKey < fromDate) return false;
  if (toDate && dateKey > toDate) return false;
  return true;
}

function postingBlockCoversDate(block = {}, dateKey = '', { allowUndated = false } = {}) {
  if (!block || !dateKey) return false;
  const fromDate = toLocalDateKey(block?.fromDate || block?.startDate || block?.from);
  const toDate = toLocalDateKey(block?.toDate || block?.endDate || block?.to);
  if (!fromDate && !toDate) {
    return allowUndated && Boolean(block?.centre || block?.center || block?.location);
  }
  if (fromDate && dateKey < fromDate) return false;
  if (toDate && dateKey > toDate) return false;
  return true;
}

function resolvedPostingCentreStillCurrent(context = {}) {
  const centreValue = normalizeCentreLabel(context?.centre || context?.center || context?.location || '');
  if (!centreValue) return false;

  const today = todayLocalDateKey();
  const fromDate = toLocalDateKey(context?.fromDate || context?.startDate || context?.from);
  const toDate = toLocalDateKey(context?.toDate || context?.endDate || context?.to);

  if (fromDate || toDate) {
    return postingBlockCoversDate(context, today);
  }

  // A resolver result without posting bounds is reusable only on the exact day
  // it was resolved. This prevents an open launcher tab from carrying yesterday's
  // posting centre into a new posting/day.
  const asOfDate = toLocalDateKey(context?.asOfDate || context?.resolvedAt);
  return Boolean(asOfDate && asOfDate === today);
}

function latestRosterAnchor(rows = [], dateKey = '') {
  const activeRows = (Array.isArray(rows) ? rows : [])
    .filter((row) => row?.isOnDuty !== false && row?.isOnLeave !== true)
    .map((row) => {
      const bounds = rosterRowDateBounds(row);
      const anchorDate = toLocalDateKey(row?.date) || bounds.fromDate || bounds.toDate;
      return { row, anchorDate };
    })
    .filter(({ row, anchorDate }) => rosterRowCentre(row) && anchorDate && (!dateKey || anchorDate <= dateKey))
    .sort((a, b) => String(b.anchorDate).localeCompare(String(a.anchorDate)));

  return activeRows[0] || null;
}

function cacheResolvedPostingCentre(value) {
  const centreValue = normalizeCentreLabel(value);
  if (!centreValue) return;
  try {
    localStorage.setItem('centre', centreValue);
    localStorage.setItem('activeCentre', centreValue);
    sessionStorage.setItem('centre', centreValue);
    sessionStorage.setItem('activeCentre', centreValue);
  } catch {}
}

async function resolveCurrentPostingCentre() {
  const today = todayLocalDateKey();

  // Preferred source: the backend's current posting-block resolver. Activity Log
  // already uses this endpoint for the intern's authoritative posting window.
  try {
    const qs = new URLSearchParams({ date: today }).toString();
    const data = await fetchJson(`/api/intern-rotation-checklists/my/current?${qs}`);
    const block = data?.postingBlock || null;
    const blockCentre = normalizeCentreLabel(block?.centre || block?.center || block?.location || '');
    if (blockCentre && postingBlockCoversDate(block, today)) {
      return {
        centre: blockCentre,
        source: 'current-posting-block',
        fromDate: toLocalDateKey(block?.fromDate || block?.startDate || block?.from),
        toDate: toLocalDateKey(block?.toDate || block?.endDate || block?.to),
        asOfDate: today,
      };
    }
  } catch (e) {
    if (isSessionExpiredError(e)) throw e;
  }

  // Fallback: resolve the row that actually covers today. Never choose the most
  // frequent historical centre and never use generic browser centre storage as
  // evidence of the intern's current posting.
  const rosterData = await fetchJson('/api/rosters?mine=1&page=1&limit=200');
  const raw = rosterData?.rows || rosterData?.items || rosterData?.data || rosterData?.rosters || rosterData;
  const rows = Array.isArray(raw) ? raw : [];
  const currentRows = rows.filter((row) => rosterRowCoversDate(row, today));

  if (currentRows.length) {
    const currentRow = currentRows
      .slice()
      .sort((a, b) => {
        const aDate = rosterRowDateBounds(a).fromDate || '';
        const bDate = rosterRowDateBounds(b).fromDate || '';
        return String(bDate).localeCompare(String(aDate));
      })[0];
    const currentCentre = rosterRowCentre(currentRow);
    const currentBounds = rosterRowDateBounds(currentRow);

    if (currentCentre) {
      // Where available, expand a daily roster anchor to its contiguous posting
      // block so refreshes/weekends retain the same authoritative centre.
      try {
        const anchorDate = toLocalDateKey(currentRow?.date) || today;
        const blockQs = new URLSearchParams({ centre: currentCentre, date: anchorDate }).toString();
        const blockData = await fetchJson(`/api/intern-rotation-checklists/my/current?${blockQs}`);
        const block = blockData?.postingBlock || null;
        const blockCentre = normalizeCentreLabel(block?.centre || currentCentre);
        if (blockCentre && postingBlockCoversDate(block, today)) {
          return {
            centre: blockCentre,
            source: 'roster-current-row+posting-block',
            fromDate: toLocalDateKey(block?.fromDate || currentBounds.fromDate),
            toDate: toLocalDateKey(block?.toDate || currentBounds.toDate),
            asOfDate: today,
          };
        }
      } catch (e) {
        if (isSessionExpiredError(e)) throw e;
      }

      return {
        centre: currentCentre,
        source: 'roster-current-row',
        fromDate: currentBounds.fromDate,
        toDate: currentBounds.toDate,
        asOfDate: today,
      };
    }
  }

  // Last safe fallback: use the latest roster row only as an anchor, then accept
  // it only if the backend posting block confirms that the block still covers today.
  const anchor = latestRosterAnchor(rows, today);
  if (anchor) {
    const anchorCentre = rosterRowCentre(anchor.row);
    try {
      const blockQs = new URLSearchParams({ centre: anchorCentre, date: anchor.anchorDate }).toString();
      const blockData = await fetchJson(`/api/intern-rotation-checklists/my/current?${blockQs}`);
      const block = blockData?.postingBlock || null;
      const blockCentre = normalizeCentreLabel(block?.centre || anchorCentre);
      if (blockCentre && postingBlockCoversDate(block, today)) {
        return {
          centre: blockCentre,
          source: 'latest-roster-anchor+posting-block',
          fromDate: toLocalDateKey(block?.fromDate),
          toDate: toLocalDateKey(block?.toDate),
          asOfDate: today,
        };
      }
    } catch (e) {
      if (isSessionExpiredError(e)) throw e;
    }
  }

  return null;
}

export default function ActivityStartWizard() {
  const role = toTitleCaseRole(readRoleFromStorage());
  const navigate = useNavigate();
  const location = useLocation();

  const [activeCitizen, setActiveCitizen] = useState(getActiveCitizen());
  const [centre, setCentre] = useState('');
  const [prefilledCentre, setPrefilledCentre] = useState(false);
  const [centreContext, setCentreContext] = useState(null);

  const [templates, setTemplates] = useState([]);
  const [tplErr, setTplErr] = useState('');
  const [tplLoading, setTplLoading] = useState(true);
  const [sessionExpired, setSessionExpired] = useState(false);

  const [error, setError] = useState('');
  const [starting, setStarting] = useState(false);
  const [launcherView, setLauncherView] = useState('quick');
  const [catalogGroupFilter, setCatalogGroupFilter] = useState('');
  const [expandedGroups, setExpandedGroups] = useState({});

  useEffect(() => {
    ensureTenantSlugStored();
    clearVisitContext(navigate, location);
    // eslint-disable-next-line
  }, []);

  useEffect(() => {
    if (centreContext?.centre) return;

    let alive = true;
    (async () => {
      try {
        const resolved = await resolveCurrentPostingCentre();
        if (!alive || !resolved?.centre) return;
        const normalized = normalizeCentreLabel(resolved.centre);
        setCentre(normalized);
        setCentreContext({ ...resolved, centre: normalized });
        setPrefilledCentre(true);
        cacheResolvedPostingCentre(normalized);
      } catch (e) {
        if (!alive) return;
        if (isSessionExpiredError(e)) setSessionExpired(true);
      }
    })();

    return () => { alive = false; };
  // eslint-disable-next-line
  }, [centreContext?.centre]);

  useEffect(() => {
    let alive = true;

    (async () => {
      try {
        setTplLoading(true);
        const loader = typeof activityApi.listActiveTemplates === 'function'
          ? activityApi.listActiveTemplates
          : activityApi.listTemplates;
        const res = await loader({ active: true, limit: 500 });
        const raw = pickArrayPayload(res);
        const norm = raw
          .map(normalizeTemplateRow)
          .filter((template) => template.title && template.active !== false);

        if (!alive) return;
        setTemplates(norm);
        setSessionExpired(false);
        setTplErr('');
      } catch (e) {
        if (!alive) return;
        if (isSessionExpiredError(e)) {
          setSessionExpired(true);
          setTplErr('');
        } else {
          setSessionExpired(false);
          setTplErr(e?.message || 'Failed to load activity templates');
        }
        setTemplates([]);
      } finally {
        if (alive) setTplLoading(false);
      }
    })();

    return () => { alive = false; };
  }, []);

  useEffect(() => {
    const i = setInterval(() => {
      const latest = getActiveCitizen();
      const curr = activeCitizen?.citizenId || '';
      if ((latest?.citizenId || '') !== curr) setActiveCitizen(latest || null);
    }, 800);
    return () => clearInterval(i);
    // eslint-disable-next-line
  }, [activeCitizen?.citizenId]);

  const goCitizenDashboard = () =>
    navigate(tenantUrl('/dashboard/operations'), {
      state: { focusCitizenSearch: true, fromActivityStart: true },
    });

  const goToTenantLogin = () => {
    navigate(getTenantLoginUrl(), {
      replace: true,
      state: { sessionExpired: true, from: location.pathname },
    });
  };

  const findTemplateByTitle = (titleLike) => {
    const label = sanitizeDisplayText(titleLike);
    if (!label) return null;

    const configured = QUICK_TEMPLATE_MATCHES[label] || QUICK_TEMPLATE_MATCHES[String(titleLike || '').trim()] || null;
    const acceptedSlugs = new Set((configured?.slugs || []).map((value) => slugifyKey(value)).filter(Boolean));
    const acceptedTitles = new Set((configured?.titles || []).map((value) => sanitizeDisplayText(value).toLowerCase()).filter(Boolean));

    if (configured) {
      return templates.find((template) => {
        const keys = normalizedTemplateKeys(template);
        if (keys.some((key) => acceptedSlugs.has(key))) return true;

        const titles = normalizedTemplateTitles(template);
        return titles.some((title) => acceptedTitles.has(title));
      }) || null;
    }

    // Future quick actions may become available automatically when the backend exposes
    // a dedicated template with the same canonical title/slug. Do not inspect goals,
    // steps, Core Practice Area text, or reporting metadata for launch resolution.
    const labelTitle = label.toLowerCase();
    const labelKey = slugifyKey(label);
    return templates.find((template) => (
      normalizedTemplateTitles(template).includes(labelTitle) ||
      normalizedTemplateKeys(template).includes(labelKey)
    )) || null;
  };


  const findTemplateForQuickItem = (item) => {
    if (!item) return null;

    const direct = templates.find((template) => quickItemMatchesTemplate(item, template));
    if (direct) return direct;

    return findTemplateByTitle(item.templateLabel || item.label);
  };


  const startByTemplateId = async (tplId, sourceTemplate = null) => {
    setError('');
    if (starting || sessionExpired) return;

    const template = sourceTemplate || templates.find((t) => String(t.id || t._id || t.templateId) === String(tplId));
    const templateId = tplId || template?._id || template?.id || template?.templateId;

    if (!looksLikeTemplateIdentifier(templateId)) {
      setError('Choose an activity template.');
      return;
    }

    if (isOpdHandoffTemplate(template)) {
      goCitizenDashboard();
      return;
    }

    setStarting(true);
    try {
      const centreResolution = await resolveEffectiveCentre();
      const effectiveCentre = normalizeCentreLabel(centreResolution?.centre || '');
      if (!effectiveCentre) {
        throw new Error('Centre could not be determined. Please make sure your current posting/roster centre is available, then try again.');
      }

      const payload = {
        templateId,
        templateSlug: template?.slug || template?.templateSlug || undefined,
        templateTitle: template?.title || template?.templateTitle || template?.templateName || undefined,
        templateName: template?.templateName || template?.name || template?.title || undefined,
        templateCategory: template?.templateCategory || template?.category || template?.finalGroup || undefined,
        activityKey: template?.activityKey || template?.slug || undefined,
        sourceFlow: template?.sourceFlow || 'guided',
        corePracticeAreaTitle: template?.corePracticeAreaTitle || template?.practiceAreaTitle || template?.finalGroup || undefined,
        practiceAreaTitle: template?.practiceAreaTitle || template?.corePracticeAreaTitle || undefined,
        finalGroup: template?.finalGroup || template?.category || undefined,
        competencies: template?.competencies || undefined,
        skills: template?.skills || undefined,
        reportingTarget: template?.reportingTarget || undefined,
        centre: effectiveCentre,
        __guidedCentreContext: {
          validated: true,
          source: centreResolution?.source || 'current-posting',
          resolvedAt: new Date().toISOString(),
          asOfDate: centreResolution?.asOfDate || todayLocalDateKey(),
          fromDate: centreResolution?.fromDate || undefined,
          toDate: centreResolution?.toDate || undefined,
        },
        citizenId: activeCitizen?.citizenId || undefined,
        citizenName: activeCitizen?.name || activeCitizen?.citizenName || undefined,
        date: new Date().toISOString(),
      };

      // Opening a guided form is deliberately provisional. The run page creates
      // the ActivityInstance only when the user enters meaningful evidence.
      navigate(
        `${tenantUrl('/intern/activity/run/new')}?templateId=${encodeURIComponent(templateId)}`,
        { state: { guidedStartPayload: payload } }
      );
    } catch (e) {
      if (isSessionExpiredError(e)) {
        setSessionExpired(true);
        setTemplates([]);
        setTplErr('');
        setError('');
      } else {
        setError(e?.message || 'Could not start activity');
      }
    } finally {
      setStarting(false);
    }
  };

  const startQuickItem = async (item) => {
    const template = findTemplateForQuickItem(item);
    const tplId = template?._id || template?.id || template?.templateId || null;
    if (!tplId) return null;
    await startByTemplateId(tplId, template);
    return tplId;
  };

  const toggleGroupExpanded = (groupId) => {
    setExpandedGroups((current) => ({
      ...current,
      [groupId]: !current?.[groupId],
    }));
  };

  const resolveEffectiveCentre = async () => {
    // A centre already resolved in this launcher render is safe to reuse. Generic
    // browser storage and citizen centre are deliberately not authoritative for
    // the staff member's current posting.
    if (centreContext?.centre && centre && resolvedPostingCentreStillCurrent(centreContext)) {
      return { ...centreContext, centre: normalizeCentreLabel(centre) };
    }

    const resolved = await resolveCurrentPostingCentre();
    if (!resolved?.centre) return null;

    const normalized = normalizeCentreLabel(resolved.centre);
    const normalizedContext = { ...resolved, centre: normalized };
    setCentre(normalized);
    setCentreContext(normalizedContext);
    setPrefilledCentre(true);
    cacheResolvedPostingCentre(normalized);
    return normalizedContext;
  };

  const normalizedCentre = useMemo(() => normalizeCentreLabel(centre), [centre]);

  const guidedTemplates = useMemo(
    () => templates.filter((template) => !isOpdHandoffTemplate(template)),
    [templates]
  );

  const quickItems = ACTIVITY_GROUPS.flatMap((group) => group.items);
  const templateCoverage = {
    totalQuickActions: quickItems.length,
    readyQuickActions: quickItems.filter((item) => Boolean(findTemplateForQuickItem(item))).length,
    catalogTemplates: guidedTemplates.length,
    trainingAreas: ACTIVITY_GROUPS.length,
  };

  const groupSummaries = ACTIVITY_GROUPS.map((group) => ({
    id: group.id,
    title: group.title,
    ready: group.items.filter((item) => Boolean(findTemplateForQuickItem(item))).length,
    total: group.items.length,
    accent: group.accent,
    soft: group.soft,
  }));

  const selectedCitizenSummary = useMemo(() => {
    if (!activeCitizen?.citizenId) return null;
    return {
      name: activeCitizen?.name || activeCitizen?.citizenName || activeCitizen?.citizenId,
      citizenId: activeCitizen?.citizenId,
      age: activeCitizen?.age || '',
      gender: activeCitizen?.gender || '',
      centre: normalizeCentreLabel(centre),
    };
  }, [activeCitizen, centre]);

  const catalogActivities = useMemo(() => {
    return guidedTemplates.map((template) =>
      templateToCatalogActivity(template, findActivityGroupForTemplate(template))
    );
  }, [guidedTemplates]);

  const catalogCategories = useMemo(() => {
    return mergeUnique([
      ...ACTIVITY_GROUPS.map((group) => group.title),
      ...catalogActivities.map((activity) => activity.category || ''),
    ]);
  }, [catalogActivities]);

  const catalogTags = useMemo(() => {
    return mergeUnique([
      'guided',
      'workflow',
      ...catalogActivities.flatMap((activity) => activity.tags || []),
      ...catalogActivities.map((activity) => activity.code || ''),
      ...catalogActivities.map((activity) => activity.category || ''),
    ]);
  }, [catalogActivities]);

  return (
    <Box className="p-6 bg-white dark:bg-[#121212] text-black dark:text-white min-h-screen space-y-4">
      <div className="flex flex-col gap-2">
        <div>
          <Typography variant="h5" className="font-semibold">Activity Launcher</Typography>
          <Typography variant="body2" className="opacity-80">
            Guided activities remain separate from the clinical Visit workflow. Use Quick Launch for day-to-day access by training area, or switch to the Full Guided Activity Catalogue to search and browse the complete active template library.
          </Typography>
        </div>
      </div>

      {error && <Alert severity="error">{error}</Alert>}
      {sessionExpired && (
        <Alert
          severity="warning"
          action={(
            <Button color="inherit" size="small" onClick={goToTenantLogin}>
              Sign in again
            </Button>
          )}
        >
          Your CMFM session has expired. Activity templates and launcher actions are locked until you sign in again.
        </Alert>
      )}
      {tplErr && <Alert severity="warning">{tplErr}</Alert>}
      {!tplLoading && !guidedTemplates.length && !tplErr && (
        <Alert severity="info">No guided activity templates were returned by the server. The launcher will remain open; seed or activate guided templates from Activity Administration.</Alert>
      )}

      <Paper variant="outlined" className="p-3">
        <div className="flex flex-col gap-3 lg:flex-row lg:items-center lg:justify-between">
          <div>
            <Typography variant="subtitle2" className="font-semibold">
              Guided activity citizen context
            </Typography>
            <Typography variant="body2" className="opacity-80">
              {selectedCitizenSummary
                ? 'This citizen will be linked when a guided activity is started. Change the citizen through Comprehensive Services when needed.'
                : 'Select a citizen through Comprehensive Services when the guided activity should be linked to an individual. Selecting a citizen does not start an OPD visit or create a guided activity record.'}
            </Typography>
          </div>
          <Button
            variant={selectedCitizenSummary ? 'outlined' : 'contained'}
            startIcon={<SearchIcon />}
            onClick={goCitizenDashboard}
            disabled={sessionExpired}
          >
            {selectedCitizenSummary
              ? 'Change Citizen in Comprehensive Services'
              : 'Select Citizen in Comprehensive Services'}
          </Button>
        </div>

        {selectedCitizenSummary ? (
          <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-3 mt-3">
            <div className="rounded-xl border border-gray-200 dark:border-gray-700 p-3">
              <Typography variant="caption" className="opacity-70">Selected citizen</Typography>
              <Typography variant="subtitle2" className="font-semibold">{selectedCitizenSummary.name}</Typography>
            </div>
            <div className="rounded-xl border border-gray-200 dark:border-gray-700 p-3">
              <Typography variant="caption" className="opacity-70">Citizen ID</Typography>
              <Typography variant="subtitle2" className="font-semibold">{selectedCitizenSummary.citizenId}</Typography>
            </div>
            <div className="rounded-xl border border-gray-200 dark:border-gray-700 p-3">
              <Typography variant="caption" className="opacity-70">Age / Gender</Typography>
              <Typography variant="subtitle2" className="font-semibold">
                {[selectedCitizenSummary.age, selectedCitizenSummary.gender].filter(Boolean).join(' • ') || '—'}
              </Typography>
            </div>
            <div className="rounded-xl border border-gray-200 dark:border-gray-700 p-3">
              <Typography variant="caption" className="opacity-70">Centre context</Typography>
              <Typography variant="subtitle2" className="font-semibold">{selectedCitizenSummary.centre || normalizedCentre || '—'}</Typography>
            </div>
          </div>
        ) : null}
      </Paper>

      {centre && (
        <Paper variant="outlined" className="p-2">
          <div className="flex items-center gap-2 flex-wrap">
            {prefilledCentre && (
              <Chip
                size="small"
                color="info"
                icon={<InfoOutlinedIcon fontSize="small" />}
                label="Prefilled from Roster"
                variant="outlined"
              />
            )}
            <Chip size="small" label={`Centre: ${normalizedCentre || centre}`} />
            <Chip size="small" variant="outlined" label={`Current templates mapped: ${templateCoverage.readyQuickActions}/${templateCoverage.totalQuickActions}`} />
            <Chip size="small" variant="outlined" label={`Guided catalogue templates: ${templateCoverage.catalogTemplates}`} />
          </div>
        </Paper>
      )}

      <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
        <Paper variant="outlined" className="p-3">
          <Typography variant="caption" className="opacity-70">Training areas</Typography>
          <Typography variant="h6">{templateCoverage.trainingAreas}</Typography>
        </Paper>
        <Paper variant="outlined" className="p-3">
          <Typography variant="caption" className="opacity-70">Current templates mapped</Typography>
          <Typography variant="h6">{templateCoverage.readyQuickActions} / {templateCoverage.totalQuickActions}</Typography>
        </Paper>
        <Paper variant="outlined" className="p-3">
          <Typography variant="caption" className="opacity-70">Guided catalogue templates</Typography>
          <Typography variant="h6">{tplLoading ? 'Loading…' : templateCoverage.catalogTemplates}</Typography>
        </Paper>
        <Paper variant="outlined" className="p-3">
          <Typography variant="caption" className="opacity-70">Launch context</Typography>
          <Typography variant="h6">{selectedCitizenSummary ? 'Citizen-linked launch' : 'Standalone launch'}</Typography>
        </Paper>
      </div>

      <Paper
        variant="outlined"
        className="p-2"
        sx={{ borderRadius: 3 }}
      >
        <Box className="flex flex-col sm:flex-row gap-2">
          <Button
            fullWidth
            variant={launcherView === 'quick' ? 'contained' : 'outlined'}
            onClick={() => setLauncherView('quick')}
            aria-pressed={launcherView === 'quick'}
            sx={{ py: 1.25, borderRadius: 2.5, textTransform: 'none', fontWeight: 700 }}
          >
            Quick Launch — Activities by Training Area
          </Button>
          <Button
            fullWidth
            variant={launcherView === 'catalog' ? 'contained' : 'outlined'}
            onClick={() => {
              setCatalogGroupFilter('');
              setLauncherView('catalog');
            }}
            aria-pressed={launcherView === 'catalog'}
            sx={{ py: 1.25, borderRadius: 2.5, textTransform: 'none', fontWeight: 700 }}
          >
            Full Guided Activity Catalogue
          </Button>
        </Box>
      </Paper>

      {launcherView === 'quick' ? (
        <Card className="dark:bg-[#232326] dark:text-white">
          <CardContent>
            <Typography variant="h6" className="font-semibold mb-1">
              Quick Launch — Activities by Training Area
            </Typography>
            <Typography variant="body2" className="opacity-80">
              Each guided template has one primary Quick Launch home. Counselling, National Health Programmes and other cross-cutting competencies may also be embedded within relevant activities without creating duplicate launcher buttons.
            </Typography>
            <Typography variant="caption" className="opacity-70 block mt-1">
              Two activities are shown in each tile by default. Use More… where available. Proposed activities that do not yet have an active backend template are not exposed as launch actions and will appear only after their controlled templates are added.
            </Typography>
            <Divider className="my-3" />

            <Box
              sx={{
                display: 'grid',
                gridTemplateColumns: {
                  xs: '1fr',
                  md: 'repeat(2, minmax(0, 1fr))',
                  lg: 'repeat(3, minmax(0, 1fr))',
                },
                gap: 2,
                alignItems: 'start',
              }}
            >
              {ACTIVITY_GROUPS.map((group) => {
                const summary = groupSummaries.find((item) => item.id === group.id);
                const ready = summary?.ready || 0;
                const total = summary?.total || 0;
                const expanded = Boolean(expandedGroups?.[group.id]);
                const visibleItems = expanded ? group.items : group.items.slice(0, 2);
                const additionalCount = Math.max(0, group.items.length - 2);

                return (
                  <Paper
                    key={group.id}
                    variant="outlined"
                    sx={{
                      p: 2,
                      borderRadius: 3,
                      borderTop: `5px solid ${group.accent}`,
                      minHeight: 285,
                      display: 'flex',
                      flexDirection: 'column',
                      boxShadow: '0 6px 20px rgba(15, 23, 42, 0.06)',
                    }}
                  >
                    <Box className="flex items-start justify-between gap-3">
                      <Box>
                        <Typography
                          variant="subtitle1"
                          fontWeight={800}
                          sx={{ color: group.accent, lineHeight: 1.25 }}
                        >
                          {group.title}
                        </Typography>
                        <Typography variant="caption" className="opacity-70 block mt-1">
                          {group.description}
                        </Typography>
                      </Box>
                      <Chip
                        size="small"
                        label={total ? `${ready} active` : 'Planned'}
                        sx={{
                          bgcolor: group.soft,
                          color: group.accent,
                          fontWeight: 700,
                          flexShrink: 0,
                        }}
                      />
                    </Box>

                    <Divider sx={{ my: 1.5 }} />

                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.75, flex: 1 }}>
                      {group.items.length ? visibleItems.map((item) => {
                        const template = findTemplateForQuickItem(item);
                        const tplId = template?._id || template?.id || template?.templateId || null;
                        const isReady = Boolean(tplId);
                        const currentTemplateTitle = sanitizeDisplayText(template?.title || template?.templateTitle || '');
                        const actionTitle = isReady
                          ? (currentTemplateTitle && currentTemplateTitle !== item.label
                            ? `Start guided activity — current template: ${currentTemplateTitle}`
                            : 'Start guided activity')
                          : 'Template not available yet';

                        return (
                          <Tooltip key={`${group.id}-${item.label}`} title={actionTitle} arrow>
                            <span>
                              <Button
                                fullWidth
                                size="small"
                                variant="text"
                                disabled={!isReady || starting || sessionExpired}
                                onClick={() => startQuickItem(item)}
                                sx={{
                                  justifyContent: 'flex-start',
                                  textAlign: 'left',
                                  textTransform: 'none',
                                  fontWeight: 600,
                                  borderRadius: 2,
                                  px: 1.25,
                                  py: 0.8,
                                  color: isReady ? group.accent : 'text.disabled',
                                  bgcolor: isReady ? group.soft : 'transparent',
                                  '&:hover': {
                                    bgcolor: isReady ? group.soft : 'transparent',
                                    boxShadow: isReady ? `inset 3px 0 0 ${group.accent}` : 'none',
                                  },
                                }}
                              >
                                {item.label}
                              </Button>
                            </span>
                          </Tooltip>
                        );
                      }) : (
                        <Box
                          sx={{
                            p: 1.5,
                            borderRadius: 2,
                            border: '1px dashed',
                            borderColor: 'divider',
                          }}
                        >
                          <Typography variant="body2" className="opacity-70">
                            No active guided template is assigned to this training area yet. The tile is reserved for the approved catalogue expansion.
                          </Typography>
                        </Box>
                      )}
                    </Box>

                    {group.items.length > 2 ? (
                      <Box className="mt-3 flex flex-wrap items-center gap-2">
                        <Button
                          size="small"
                          variant="text"
                          onClick={() => toggleGroupExpanded(group.id)}
                          sx={{
                            textTransform: 'none',
                            borderRadius: 2,
                            color: group.accent,
                            fontWeight: 700,
                          }}
                        >
                          {expanded ? 'Show less' : `More… (${additionalCount})`}
                        </Button>
                      </Box>
                    ) : null}
                  </Paper>
                );
              })}
            </Box>
          </CardContent>
        </Card>
      ) : (
        <Card className="dark:bg-[#232326] dark:text-white">
          <CardContent>
            <Box className="flex flex-col gap-2 md:flex-row md:items-start md:justify-between">
              <Box>
                <Typography variant="h6" className="font-semibold mb-1">
                  Full Guided Activity Catalogue
                </Typography>
                <Typography variant="body2" className="opacity-80">
                  Search and filter the complete active guided-template library. The same underlying template is launched whether you enter from Quick Launch or from this catalogue.
                </Typography>
                <Typography variant="caption" className="opacity-70 block mt-1">
                  {tplLoading ? 'Loading guided activity catalogue…' : `${templateCoverage.catalogTemplates} guided template(s) available.`}
                </Typography>
              </Box>

              {catalogGroupFilter ? (
                <Chip
                  label={`Filtered: ${catalogGroupFilter}`}
                  onDelete={() => setCatalogGroupFilter('')}
                  sx={{ alignSelf: { xs: 'flex-start', md: 'center' }, fontWeight: 600 }}
                />
              ) : null}
            </Box>

            <Divider className="my-3" />

            <ActivityCatalogGrid
              key={`activity-catalog-${catalogGroupFilter || 'all'}`}
              citizenId={activeCitizen?.citizenId || null}
              visitId={null}
              userRole={role}
              hideUnavailable={false}
              activities={catalogActivities}
              allowCategories={catalogCategories}
              allowTags={catalogTags}
              initialFilter={{
                query: '',
                categories: catalogGroupFilter ? [catalogGroupFilter] : [],
                tags: [],
              }}
              disabled={sessionExpired}
              onStart={async (activity) => {
                if (sessionExpired) return;
                try {
                  if (!activity.templateId) {
                    if (activity.guidelineHref) {
                      window.open(activity.guidelineHref, '_blank', 'noopener');
                    }
                    return;
                  }
                  await startByTemplateId(activity.templateId, activity._rawTemplate || activity);
                } catch (e) {
                  setError(e?.message || 'Could not start activity');
                }
              }}
            />
          </CardContent>
        </Card>
      )}
    </Box>
  );
}
